<template>
  <div class="voucher-print-container" v-if="voucherData && voucherData.voucherNo">
    <!-- Header Section -->
    <div class="voucher-header">
      <div class="logo-section">
        <div class="company-text">
          <img :src="logoImage1" alt="Nestoria Group" class="company-logo" @error="handleImageError" />
          <img :src="logoImage2" alt="Nestoria Group Text" class="company-logo-text" @error="handleImageError" />
        </div>
      </div>
      <div class="header-right">
        <div class="company-name">Nestoria Buildcon Pvt. Ltd.</div>
        <div class="voucher-title">Payment Voucher</div>
      </div>
    </div>

    <!-- Form-Style Information Fields -->
    <div class="voucher-info-fields">
      <!-- PAID TO, NO., DATE - All on Same Line -->
      <div class="form-field-inline">
        <div class="inline-field-group">
          <span class="form-label">PAID TO</span>
          <span class="form-value-inline-paid">{{ voucherData.paidTo || '' }}</span>
        </div>
        <div class="inline-field-group">
          <span class="form-label">NO.:</span>
          <span class="form-value-inline">{{ voucherData.voucherNo }}</span>
        </div>
        <div class="inline-field-group">
          <span class="form-label">DATE:</span>
          <span class="form-value-inline">{{ voucherData.date }}</span>
        </div>
      </div>
      
      <!-- DEBIT - Separate Line -->
      <div class="form-field">
        <span class="form-label">DEBIT</span>
        <span class="form-value">{{ voucherData.debit || '' }}</span>
      </div>
      
      <!-- ON A/C OF - Separate Line -->
      <div class="form-field">
        <span class="form-label">ON A/C OF</span>
        <span class="form-value">{{ voucherData.onAccountOf || '' }}</span>
      </div>
    </div>

    <!-- Particulars Table (ONLY This Section Uses Table) -->
    <table class="particulars-table">
      <thead>
        <tr class="particulars-header">
          <th class="particulars-col"><strong><u>PARTICULARS:</u></strong></th>
          <th class="rupees-col"><strong>Rs.</strong></th>
          <th class="paise-col"><strong>Ps.</strong></th>
        </tr>
      </thead>
      <tbody>
        <!-- Particulars Rows (Max 4 rows) -->
        <template v-if="voucherData.particulars && voucherData.particulars.length > 0">
          <tr v-for="(item, index) in voucherData.particulars.slice(0, 4)" :key="index">
            <td>{{ getParticularName(item, index) }}</td>
            <td class="text-right">{{ item.rupees || '' }}</td>
            <td class="text-right">{{ item.paise || '' }}</td>
          </tr>
        </template>

        <!-- Fill empty rows if less than 4 -->
        <template v-if="!voucherData.particulars || voucherData.particulars.length < 4">
          <tr v-for="index in (4 - (voucherData.particulars ? voucherData.particulars.length : 0))" :key="`empty-${index}`">
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </template>

        <!-- RUPEES IN WORDS Row -->
        <tr class="amount-in-words-row">
          <td colSpan="3"><strong>RUPEES IN WORDS:</strong> {{ voucherData.amountInWords || '' }}</td>
        </tr>

        <!-- TOTAL Row -->
        <tr class="total-row">
          <td class="text-right"><strong>TOTAL</strong></td>
          <td class="text-right"><strong>{{ voucherData.totalRupees || '' }}</strong></td>
          <td class="text-right"><strong>{{ voucherData.totalPaise || '' }}</strong></td>
        </tr>
      </tbody>
    </table>

    <!-- Footer Section -->
    <div class="voucher-footer">
      <div class="footer-left">
        <div class="footer-item">
          <p><strong>Authorized by L1</strong></p>
          <div class="signature-line">
            {{ voucherData.approverL1 || '' }}
          </div>
        </div>
        <div class="footer-item">
          <p><strong>Prepared By</strong></p>
          <div class="signature-line">
            {{ voucherData.preparedBy || '' }}
          </div>
        </div>
      </div>
      <div class="footer-right">
        <div class="footer-item">
          <p><strong>Authorized by L2</strong></p>
          <div class="signature-line">
            {{ voucherData.approverL2 || '' }}
          </div>
        </div>
        <div class="footer-item">
          <p><strong>RECEIVER'S SIGN</strong></p>
          <div class="signature-box"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logo from "../../../public/logo.png";
import logotxt from "../../assets/images/logo/ngerplogotxt.png";

export default {
  props: ["voucherData"],
  data() {
    return {
      baseUrl: window.location.origin,
      webEndpoint: process.env.VUE_APP_WEBENDPOINT || window.location.origin,
      logo,
      logotxt,
    };
  },
  computed: {
    logoImage1() {
      // Use the same logo as navbar-brand
      return this.logo;
    },
    logoImage2() {
      // Use the same logo text as navbar-brand
      return this.logotxt;
    }
  },
  methods: {
    getParticularName(item, index) {
      // If particular is empty, use fallback from voucher object
      if (item.particular) {
        return item.particular;
      }
      // Fallback to voucher_items data
      if (this.voucherData.voucher && this.voucherData.voucher.voucher_items && this.voucherData.voucher.voucher_items[index]) {
        const voucherItem = this.voucherData.voucher.voucher_items[index];
        if (voucherItem.ledger) {
          return voucherItem.ledger.fullname || voucherItem.ledger.name || '';
        }
      }
      return '';
    },
    handleImageError(event) {
      // Fallback to base URL if web endpoint fails
      if (event.target.src.includes(this.webEndpoint)) {
        event.target.src = `${this.baseUrl}/receipt_assets/${event.target.alt === 'Logo' ? 'screenshot.png' : 'image001.png'}`;
      } else {
        // Hide image if both paths fail
        event.target.style.display = 'none';
      }
    }
  }
};
</script>

<style scoped>
.voucher-print-container {
  width: 800px;
  margin: 15px auto;
  padding: 30px 40px;
  background: white;
  font-family: Arial, sans-serif;
  border: 2px solid #004D7B;
  border-radius: 8px;
}

/* Header Section */
.voucher-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #004D7B;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-img {
  max-height: 60px;
  width: auto;
}

.company-text {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.company-logo {
  max-height: 30px;
  width: auto;
}

.company-logo-text {
  max-width: 100%;
  height: auto;
  max-height: 20px;
  width: auto;
  margin-top: 2px;
}

.tagline {
  margin: 0;
  font-size: 11px;
  color: #004D7B;
  font-weight: 600;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.company-name {
  font-size: 24px;
  font-weight: bold;
  color: #004D7B;
  text-align: right;
}

.voucher-title {
  font-size: 18px;
  font-weight: bold;
  color: #004D7B;
  text-align: right;
  background: linear-gradient(circle, #2aa450, #009075, #00798a, #005f88, #074470);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.voucher-number-date {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 5px;
}

.voucher-number-date .label {
  font-weight: bold;
  color: #004D7B;
  font-size: 12px;
}

.voucher-number-date .underline-value {
  border-bottom: 1px solid #004D7B;
  min-width: 80px;
  padding-bottom: 2px;
  color: #004D7B;
  font-size: 12px;
  display: inline-block;
}

/* Form-Style Information Fields */
.voucher-info-fields {
  margin: 25px 0;
}

.form-field {
  display: flex;
  align-items: baseline;
  margin: 15px 0;
}

.form-field-inline {
  display: flex;
  align-items: baseline;
  gap: 20px;
  margin: 15px 0;
  flex-wrap: nowrap;
}

.inline-field-group {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.form-label {
  font-weight: bold;
  color: #004D7B;
  min-width: 80px;
  font-size: 13px;
  white-space: nowrap;
}

.form-value {
  flex: 1;
  border-bottom: 1px solid #004D7B;
  padding-bottom: 3px;
  min-height: 20px;
  color: #004D7B;
  font-size: 13px;
}

.form-value-inline {
  border-bottom: 1px solid #004D7B;
  min-width: 120px;
  padding-bottom: 3px;
  min-height: 20px;
  color: #004D7B;
  font-size: 13px;
  display: inline-block;
}

.form-value-inline-paid {
  border-bottom: 1px solid #004D7B;
  min-width: 150px;
  padding-bottom: 3px;
  min-height: 20px;
  color: #004D7B;
  font-size: 13px;
  display: inline-block;
  flex: 1;
}

/* Particulars Table (ONLY This Section Uses Table) */
.particulars-table {
  width: 100%;
  border-collapse: collapse;
  border: 2px solid #004D7B;
  border-radius: 8px;
  margin: 25px 0;
  overflow: hidden;
}

.particulars-table th,
.particulars-table td {
  border: 1px solid #004D7B;
  padding: 12px 10px;
  color: #004D7B;
  font-size: 12px;
  height: 45px;
}

.particulars-table th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.particulars-table th strong {
  color: #004D7B;
}

.particulars-col {
  width: 60%;
}

.rupees-col {
  width: 20%;
  text-align: center;
}

.paise-col {
  width: 20%;
  text-align: center;
}

.text-right {
  text-align: right;
}

.amount-in-words-row {
  background-color: #f0f0f0;
}

.amount-in-words-row td {
  padding: 12px 10px;
  font-weight: bold;
}

.total-row {
  background-color: #f0f0f0;
  font-weight: bold;
}

.total-row td {
  font-weight: bold;
}

/* Footer Section */
.voucher-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #004D7B;
}

.footer-left,
.footer-right {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 45%;
}

.footer-right {
  align-items: flex-end;
}

.footer-item {
  display: flex;
  flex-direction: column;
}

.footer-item p {
  margin: 0 0 5px 0;
  font-size: 12px;
  color: #004D7B;
  font-weight: bold;
}

.signature-line {
  border-bottom: 1px solid #004D7B;
  width: 200px;
  padding-top: 5px;
  min-height: 20px;
  font-size: 12px;
  color: #004D7B;
}

.signature-box {
  border: 1px solid #6d6357;
  border-radius: 7px;
  width: 150px;
  height: 70px;
}

/* Print-specific styles */
@media print {
  .voucher-print-container {
    width: 100%;
    margin: 0;
    padding: 30px 40px;
    border: 2px solid #004D7B;
  }

  .voucher-header {
    page-break-inside: avoid;
  }

  .voucher-info-fields {
    page-break-inside: avoid;
  }

  .particulars-table {
    page-break-inside: avoid;
  }

  .voucher-footer {
    page-break-inside: avoid;
  }
}
</style>

